import React from "react";
import axios from "axios";
import { getSearchParams, getAuthProfile, authSessionExpiry } from "../services/utils/validation";
import { clearRequestCache } from "../services/utils/requestCache";
import { LoaderOverlay } from "../services/utils/BouncingDots";

/* ==========================================
   AUTH HELPERS
========================================== */

export const redirectToLogin = async () => {
    try {
        const res = await axios.get(
            `setting/getaccountsetting/-/${encodeURIComponent("EEI,DUS,LRG")}`
        );

        const settings = res?.data?.data ?? [];

        const eei = settings.find(x => x.settingCode === "EEI")?.value;
        const dus = settings.find(x => x.settingCode === "DUS")?.value;
        const lrg = settings.find(x => x.settingCode === "LRG")?.value?.trim();

        if (eei === "1" && dus === "1" && lrg) {
            const currentUrl = window.location.href;

            window.location.href =
                `${lrg}?returnUrl=${currentUrl}&action=session_expired`;

            return true;
        }
    } catch (error) {
        console.error("Failed to load login settings", error);
    }

    return false;
};

// Set the instant a 401/403 is confirmed — before any further `await` — so any code still
// running in the same tick (e.g. Service.js's REC step-machine, which keeps executing after
// `window.location.href` is merely *assigned*; the current script isn't torn down until the
// browser actually completes the navigation) can check this and bail out instead of making
// further step decisions. Without it, a call like checkUserProfile() that 401s here resolves
// to a "normal-looking" (if falsy) result instead of an aborted one, and the caller — unaware
// the page is already navigating away — proceeds to decide/record a step (e.g. PUP) the user
// never actually saw, corrupting serviceFlowHistory for the rest of the session.
let _authRedirecting = false;
export const isAuthRedirecting = () => _authRedirecting;

const handleAuthError = async (error, url, preserveRedirect = false) => {
    const statusCode = error?.response?.data?.statusCode;

    // Several requests can 401 together (e.g. Service.js's REC step fires checkuserprofile and
    // a family-list fetch in the same Promise.all for an anonymous user). Each one's catch block
    // calls this independently, and without this guard every one of them would run its own
    // clearAuth/redirect logic and assign window.location.href -- whichever settles LAST wins,
    // since the script keeps running until the browser actually completes the navigation. That
    // made the destination (e.g. /security/signup vs. /security/login) pick itself at random by
    // network timing instead of by which page is actually correct. Only the first 401/403 to
    // arrive gets to decide.
    if (_authRedirecting) return true;

    if (statusCode === 401) {
        _authRedirecting = true;
        const currentPage = getSearchParams("current");

        clearAuth(preserveRedirect);

        const redirected = await redirectToLogin();

        if (redirected) {
            return true;
        }

        if (
            url === "user/checkuserprofile" &&
            (currentPage === "REC" || currentPage === "COM")
        ) {
            window.location.href = "/security/signup";
            return true;
        }

        window.location.href = "/security/login";
        return true;
    }

    if (statusCode === 403) {
        _authRedirecting = true;
        clearAuth(true);
        window.location.href = "/page-access-denied";
        return true;
    }

    return false;
};

/* ==========================================
   API METHODS
========================================== */

export const serverGet = async (url) => {
    try {
        const result = await axios.get(url);
        return result.data;
    } catch (error) {
        const handled = await handleAuthError(error, url);

        if (!handled) {
            console.error(error);
            return error?.response;
        }
    }
};

export const serverPost = async (url, params) => {
    try {
        const result = await axios({
            method: "POST",
            url,
            data: { value: JSON.stringify(params) }
        });

        return result.data;
    } catch (error) {
        const handled = await handleAuthError(error, url);

        if (!handled) {
            console.error(error);
            return error?.response;
        }
    }
};

export const serverAPIPost = async (
    url,
    params,
    canRedirect = true
) => {
    try {
        const result = await axios({
            method: "POST",
            url,
            data: params
        });

        return result.data;
    } catch (error) {
        if (canRedirect) {
            const handled = await handleAuthError(error, url);

            if (!handled) {
                console.error(error);
                return error?.response;
            }
        } else {
            return error?.response;
        }
    }
};

export const serverFormPost = async (
    url,
    params,
    canRedirect = true
) => {
    try {
        const result = await axios({
            method: "POST",
            url,
            data: params,
            headers: {
                "Content-Type": "multipart/form-data"
            }
        });

        return result.data;
    } catch (error) {
        if (canRedirect) {
            const handled = await handleAuthError(error, url);

            if (!handled) {
                console.error(error);
                return error?.response;
            }
        } else {
            return error?.response;
        }
    }
};

export const serverPut = async (url, params) => {
    try {
        const result = await axios({
            method: "PUT",
            url,
            data: { value: JSON.stringify(params) }
        });

        return result.data;
    } catch (error) {
        const handled = await handleAuthError(error, url);

        if (!handled) {
            console.error(error);
            return error?.response;
        }
    }
};

export const serverDelete = async (url, params) => {
    try {
        const result = await axios({
            method: "DELETE",
            url,
            data: { value: JSON.stringify(params) }
        });

        return result.data;
    } catch (error) {
        const handled = await handleAuthError(error, url);

        if (!handled) {
            console.error(error);
            return error?.response;
        }
    }
};

// Extracts the server-suggested filename from a Content-Disposition response header,
// per RFC 6266/5987. Prefers the extended `filename*=UTF-8''...` form (percent-encoded,
// round-trips non-ASCII names correctly) over the plain quoted `filename="..."` form.
// Returns null if the header is absent or no filename could be parsed.
export const parseContentDispositionFilename = (contentDisposition) => {
    if (!contentDisposition) return null;

    const extendedMatch = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    if (extendedMatch) {
        try {
            const decoded = decodeURIComponent(extendedMatch[1].trim());
            if (decoded) return decoded;
        } catch (e) {
            // malformed percent-encoding — fall through to try the plain form
        }
    }

    const plainMatch = contentDisposition.match(/filename\s*=\s*"((?:[^"\\]|\\.)*)"|filename\s*=\s*([^;]+)/i);
    if (plainMatch) {
        const raw = (plainMatch[1] ?? plainMatch[2] ?? "").trim();
        if (raw) return raw.replace(/\\(["\\])/g, "$1");
    }

    return null;
};

// Strips path separators/control characters as defense-in-depth before using an
// externally-sourced (Akute) filename as a suggested save name. The `download`
// attribute never executes this string — it's only ever shown as a filename.
const sanitizeDownloadFilename = (name) =>
    name.replace(/[\\/:*?"<>|\x00-\x1F]/g, "_").trim();

export const serverDownloadFile = async (url, doc_name) => {
    try {
        const response = await axios.get(url, {
            responseType: "arraybuffer",
            headers: {
                "Content-Type": "application/json"
            }
        });

        const headerFilename = parseContentDispositionFilename(
            response.headers?.["content-disposition"]
        );
        const resolvedName = doc_name
            ? sanitizeDownloadFilename(doc_name)
            : headerFilename
                ? sanitizeDownloadFilename(headerFilename)
                : '';

        const blob = new Blob([response.data], {
            type: "application/octet-stream"
        });

        const objUrl = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = objUrl;
        link.setAttribute("download", resolvedName);

        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(objUrl);
    } catch (error) {
        const handled = await handleAuthError(error, url);

        if (!handled) {
            console.error(error);
            return error?.response;
        }
    }
};

/* ==========================================
   LOADING COMPONENT
========================================== */

export const Prog = (props) => {
    return props.loading ? <LoaderOverlay text="Loading..." /> : null;
};

/* ==========================================
   AUTH CLEAR
========================================== */

export const clearAuth = (preserveRedirect = false) => {
    if (!preserveRedirect) {
        const currentURL =
            `${window.location.pathname}${window.location.search}`;

        sessionStorage.setItem(
            "redirectAuthentication",
            JSON.stringify(currentURL)
        );

        // A live session that just died is marked so the auth page knows it interrupted the patient
        // rather than being the flow's own login step. The absence of isAuth is NOT the same as "this
        // is a guest": a patient who logged out in this tab has no isAuth either, and clearing the
        // marker here would let that case read as a guest and put the intake progress bar on the
        // login page. Only a successful sign-in clears it (Login.js), so a stale marker cannot
        // outlive the session it describes.
        if (getAuthProfile.getProfileAuth()) {
            authSessionExpiry.mark();
        }
    }

    localStorage.removeItem("memberId");

    // A session that ends without a page load leaves the cache's in-memory maps alive, so whoever
    // signs in next in this tab would read the previous session's answers. Clearing storage alone
    // does not cover that — the maps have to go too.
    clearRequestCache();

    sessionStorage.removeItem("redirectUrl");
    sessionStorage.removeItem("isAuth");

    sessionStorage.setItem("isAuth", false);
};